import React, { useEffect, useRef, useState } from 'react';
import { Pressable, View, Text, TextInput, StyleSheet, ScrollView, Platform } from 'react-native';
import Svg, { Polygon } from 'react-native-svg';
import { useTheme, makeThemedStyles, radii, SECTOR_COLOR } from '../../v10/theme';
import { GlassCard } from '../../v10/ui';
import type { Sector } from '../../domain/olmps';
import { STEP_STATUS_META, effectiveStatus, type StepStatus } from '../stepStatus';

export interface PipelineCheckItem {
  id: string;
  text: string;
  checked: boolean;
  /** Pending/Active/Completed — the same field the Directions rows edit. */
  status?: StepStatus;
  substeps?: PipelineCheckItem[];
  dueDate?: string;
}

export interface PipelineProject {
  id: string;
  project_title: string;
  sector?: Sector | null;
  basic_stages: PipelineCheckItem[];
}


const CIRCLE_DIAM = 88;
const COL_WIDTH = 124;
const ARROW_LEN = 36;
// Right edge of the circle, in column-relative x — the arrow must start here
// (not at the column's horizontal center) or it draws straight through the
// circle's own label instead of bridging the gap to the next circle.
const CIRCLE_RIGHT_EDGE = (COL_WIDTH + CIRCLE_DIAM) / 2;

// Same height as the stage circles, so the badge sits on the same centerline
// as the pipeline it leads into.
const HEX_HEIGHT = CIRCLE_DIAM;
// Regular pentagon, point up. For circumradius R the width is 2R·sin72° and
// the height R(1+cos36°), so width is derived from height (PENT_PAD is the
// inset that keeps the rounded stroke-join inside the box). Names keep the old
// HEX_ prefix — layout styles key off them.
const PENT_PAD = 5;
const PENT_R = (HEX_HEIGHT - 2 * PENT_PAD) / (1 + Math.cos(Math.PI / 5));
const HEX_WIDTH = Math.round(2 * PENT_R * Math.sin((2 * Math.PI) / 5) + 2 * PENT_PAD);
const HEX_FILL = '#8FA532'; // olive — matches the ORDER "Execute" stage color

/** Colour comes straight from the shared STEP_STATUS_META palette keyed off the
 *  row's own status, so a stage/sub-step reads the SAME colour here as it does
 *  in the TKNE Directions list. (This used to be derived positionally —
 *  done/active/future — which drifted from what the list showed.) */
function statusFill(item: PipelineCheckItem): string {
  return STEP_STATUS_META[effectiveStatus(item)].color;
}

/** Blend #rrggbb `a` toward `b` (0 = all a, 1 = all b). Used to sink the
 *  bright sector colour into the page's navy so the solid fill reads as a
 *  deep jewel tone that sits with the navy/gold theme instead of clashing. */
function mix(a: string, b: string, amt: number): string {
  const pa = /^#?([0-9a-f]{6})$/i.exec(a), pb = /^#?([0-9a-f]{6})$/i.exec(b);
  if (!pa || !pb) return a;
  const na = parseInt(pa[1], 16), nb = parseInt(pb[1], 16);
  const ch = (sh: number) => Math.round(((na >> sh) & 255) * (1 - amt) + ((nb >> sh) & 255) * amt);
  return `#${((1 << 24) | (ch(16) << 16) | (ch(8) << 8) | ch(0)).toString(16).slice(1)}`;
}

function formatShortDate(iso: string): string {
  // Expects yyyy-MM-dd; render as M/D. Falls back to raw string if unparsable.
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const month = parseInt(m[2], 10);
  const day = parseInt(m[3], 10);
  return `${month}/${day}`;
}

/** Rounded regular pentagon badge holding the project title.
 *  Solid deep jewel tone (sector colour sunk into navy, olive if none) +
 *  thin solid gold rim + soft shadow + ivory title — no dashes. */
function ProjectHexagon({ title, fill, editing, onSubmitTitle }: {
  title: string;
  fill: string;
  /** Rename in place: the label becomes an input; Enter/blur commits. */
  editing?: boolean;
  onSubmitTitle?: (title: string) => void;
}) {
  const { t } = useTheme();
  const styles = useStyles();
  const [draft, setDraft] = useState(title);
  // Enter fires submit AND the blur that follows — commit only once.
  const committed = useRef(false);
  useEffect(() => { if (editing) { setDraft(title); committed.current = false; } }, [editing, title]);
  const commit = () => {
    if (committed.current) return;
    committed.current = true;
    onSubmitTitle?.(draft);
  };
  const face = mix(fill, t.navy, 0.52);
  const cx = HEX_WIDTH / 2, cy = PENT_PAD + PENT_R;
  const pts = [0, 1, 2, 3, 4].map((k) => {
    const a = -Math.PI / 2 + (k * 2 * Math.PI) / 5;
    return [cx + PENT_R * Math.cos(a), cy + PENT_R * Math.sin(a)];
  });
  const points = pts.map((q) => q.join(',')).join(' ');
  const shadow = pts.map(([x, y]) => `${x},${y + 3}`).join(' ');
  return (
    <View style={styles.hexWrap}>
      <Svg width={HEX_WIDTH} height={HEX_HEIGHT + 4}>
        <Polygon points={shadow} fill={t.glowSoft} stroke={t.glowSoft} strokeWidth={10} strokeLinejoin="round" />
        {/* Gold rim = the stroke ring; the solid project colour is drawn over
            it slightly thinner so ~1.5px of gold shows around the face. */}
        <Polygon points={points} fill={t.gold} stroke={t.gold} strokeWidth={10} strokeLinejoin="round" />
        <Polygon points={points} fill={face} stroke={face} strokeWidth={7} strokeLinejoin="round" />
      </Svg>
      {editing ? (
        <View style={styles.hexLabelWrap}>
          <TextInput
            style={styles.hexInput}
            value={draft}
            onChangeText={setDraft}
            onSubmitEditing={commit}
            onBlur={commit}
            autoFocus
            selectTextOnFocus
          />
        </View>
      ) : (
        <View style={styles.hexLabelWrap} pointerEvents="none">
          <Text style={styles.hexLabelText} numberOfLines={2} ellipsizeMode="tail">
            {(title || 'Untitled').toUpperCase()}
          </Text>
        </View>
      )}
    </View>
  );
}

interface ProjectPipelineProps {
  project: PipelineProject;
  onOpen: () => void;
  /** Tapping a stage circle — deep-links to that step in the editor. Falls back to onOpen. */
  onOpenStage?: (stageId: string) => void;
  /** Tapping a sub-box — deep-links to that sub-step in the editor. Falls back to onOpen. */
  onOpenSubstep?: (stageId: string, substepId: string) => void;
  onMenuPress?: () => void;
  /** 'card' (default) = the raised full-width row; 'bare' = no card chrome,
   *  for the TKNE list's group columns where the hexagon IS the item. */
  variant?: 'card' | 'bare';
  /** Bare variant only: when true just the badge shows; tapping it calls
   *  onToggleCollapsed to extend the stages out to the right (double-tap opens). */
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  /** Bare variant: edit the hexagon's title in place. */
  editingTitle?: boolean;
  onSubmitTitle?: (title: string) => void;
}

// Window for telling a double-tap (open) from a single tap (expand/collapse).
const DOUBLE_TAP_MS = 260;

export function ProjectPipeline({
  project, onOpen, onOpenStage, onOpenSubstep, onMenuPress,
  variant = 'card', collapsed = false, onToggleCollapsed, editingTitle = false, onSubmitTitle,
}: ProjectPipelineProps) {
  const { t } = useTheme();
  const styles = useStyles();
  const stages = project.basic_stages || [];
  const hexFill = project.sector ? SECTOR_COLOR[project.sector] : HEX_FILL;
  const bare = variant === 'bare';

  // Single tap expands/collapses the steps immediately; a second tap inside
  // the window opens the project. The double-tap undoes the first tap's
  // toggle so opening doesn't leave the badge in a flipped state.
  const lastTap = useRef(0);
  const onHexPress = () => {
    if (!onToggleCollapsed) { onOpen(); return; }
    const now = Date.now();
    if (now - lastTap.current < DOUBLE_TAP_MS) {
      lastTap.current = 0;
      onToggleCollapsed();
      onOpen();
      return;
    }
    lastTap.current = now;
    onToggleCollapsed();
  };
  // Right-click → the same options menu as long-press (web only; RN-web
  // forwards onContextMenu to the DOM node).
  const contextMenuProps = Platform.OS === 'web' && onMenuPress
    ? { onContextMenu: (e: any) => { e.preventDefault?.(); onMenuPress(); } }
    : {};

  const hexagon = bare ? (
    <Pressable onPress={onHexPress} onLongPress={onMenuPress} disabled={editingTitle} {...(contextMenuProps as any)}>
      <ProjectHexagon title={project.project_title} fill={hexFill} editing={editingTitle} onSubmitTitle={onSubmitTitle} />
    </Pressable>
  ) : (
    <ProjectHexagon title={project.project_title} fill={hexFill} />
  );

  if (bare && collapsed) {
    return <View style={styles.bareContainer}>{hexagon}</View>;
  }

  const Bar = bare ? View : ScrollView;
  const barProps = bare
    ? { style: [styles.orbBar, styles.orbBarBare] }
    : { horizontal: true, showsHorizontalScrollIndicator: false, style: styles.orbScroll, contentContainerStyle: styles.orbBar };

  const body = (
    <Bar {...(barProps as any)}>
        <View style={styles.hexColumn}>
          {hexagon}
          {stages.length > 0 && (
            <View style={styles.hexArrowConnector} pointerEvents="none">
              <View style={styles.arrowLine} />
              <Text style={styles.arrowHead}>{'▶'}</Text>
            </View>
          )}
        </View>

        {stages.length === 0 ? (
          <View style={styles.emptyPipeline}>
            <Text style={styles.emptyPipelineText}>No stages yet</Text>
          </View>
        ) : (
          stages.map((stage, idx) => {
            const substeps = stage.substeps || [];
            const label = `${idx + 1}. ${(stage.text || 'STAGE').toUpperCase()}`;

            return (
              <View key={stage.id} style={styles.stageColumn}>
                {/* Circle — tapping jumps straight to this step in the editor. */}
                <Pressable
                  onPress={(e) => {
                    // Nested inside the card's own onOpen Pressable (and a
                    // horizontal ScrollView) — without this the tap bubbles
                    // up on web and onOpen fires alongside/instead of the
                    // deep-link, or does nothing if onOpen is a no-op.
                    e.stopPropagation?.();
                    onOpenStage ? onOpenStage(stage.id) : onOpen();
                  }}
                  hitSlop={4}
                  style={[styles.stageOrb, { backgroundColor: statusFill(stage) }]}
                >
                  <Text
                    style={[styles.stageOrbText, styles.onStatusFill]}
                    numberOfLines={2}
                    ellipsizeMode="tail"
                  >
                    {label}
                    {stage.dueDate ? `\n${formatShortDate(stage.dueDate)}` : ''}
                  </Text>
                </Pressable>

                {/* Arrow to the next circle, anchored on the circles' shared
                    centerline and overlapping the gap into the next column. */}
                {idx < stages.length - 1 && (
                  <View style={styles.arrowConnector} pointerEvents="none">
                    <View style={styles.arrowLine} />
                    <Text style={styles.arrowHead}>{'▶'}</Text>
                  </View>
                )}

                {/* Sub-boxes hanging below every circle */}
                {substeps.length > 0 && (
                  <View style={styles.subBoxContainer}>
                    <View style={styles.subBoxConnector} />
                    {substeps.map((sub, subIdx) => (
                      <React.Fragment key={sub.id}>
                        {subIdx > 0 && <View style={styles.subBoxConnectorShort} />}
                        <Pressable
                          onPress={(e) => {
                            e.stopPropagation?.();
                            onOpenSubstep ? onOpenSubstep(stage.id, sub.id) : onOpen();
                          }}
                          hitSlop={4}
                          style={[styles.subBox, { backgroundColor: statusFill(sub) }]}
                        >
                          <Text
                            style={[styles.subBoxText, styles.onStatusFill]}
                            numberOfLines={1}
                            ellipsizeMode="tail"
                          >
                            {sub.text || `${idx + 1}.${subIdx + 1}`}
                          </Text>
                        </Pressable>
                      </React.Fragment>
                    ))}
                  </View>
                )}
              </View>
            );
          })
        )}
    </Bar>
  );

  if (bare) return <View style={styles.bareContainer}>{body}</View>;

  return (
    <GlassCard style={styles.pipelineContainer}>
    <Pressable onPress={onOpen} onLongPress={onMenuPress}>
      {onMenuPress ? (
        <Pressable
          onPress={(e) => {
            // Nested inside the row's own Pressable — without this the tap
            // bubbles up on web and also fires onOpen, so the menu opens and
            // is instantly replaced by the design screen.
            e.stopPropagation();
            onMenuPress();
          }}
          hitSlop={10}
          style={styles.menuBtn}
        >
          <Text style={styles.menuDots}>⋯</Text>
        </Pressable>
      ) : null}
      {body}
    </Pressable>
    </GlassCard>
  );
}

const useStyles = makeThemedStyles(({ t, c }) =>
  StyleSheet.create({
  pipelineContainer: {
    borderRadius: radii.panel,
    paddingVertical: 12,
    paddingHorizontal: 12,
    marginBottom: 16,
    width: '100%',
    alignSelf: 'stretch',
    position: 'relative',
  },
  bareContainer: {
    alignSelf: 'flex-start',
  },
  // Bare (column) mode: left-aligned so the stages extend rightward from the
  // hexagon, and no min-height so a collapsed→expanded row doesn't jump.
  orbBarBare: {
    flexGrow: 0,
    justifyContent: 'flex-start',
    paddingVertical: 0,
    paddingHorizontal: 0,
    minHeight: 0,
  },
  menuBtn: {
    position: 'absolute',
    top: 6,
    right: 8,
    zIndex: 2,
    padding: 4,
  },
  menuDots: { fontSize: 18, fontWeight: '800', color: t.ivorySoft },
  emptyPipeline: {
    height: HEX_HEIGHT,
    justifyContent: 'center',
    paddingHorizontal: 8,
  },
  emptyPipelineText: {
    fontSize: 12,
    color: t.ivorySoft,
    fontStyle: 'italic',
  },
  orbScroll: {
    width: '100%',
  },
  orbBar: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    // flexGrow + center: when the diagram's natural content width is
    // narrower than the card, the whole row centers in the available space
    // instead of hugging the left edge. Column widths/gaps stay FIXED
    // (COL_WIDTH) so the connector arrows — which are fixed-length and
    // positioned assuming fixed spacing — always still reach; stretching
    // the gaps (space-between) breaks them. When content overflows, this
    // has no effect and the ScrollView scrolls normally.
    flexGrow: 1,
    justifyContent: 'center',
    paddingVertical: 8,
    paddingHorizontal: 4,
    minHeight: 160,
  },
  hexColumn: {
    width: HEX_WIDTH + 20,
    alignItems: 'center',
    position: 'relative',
  },
  hexWrap: {
    width: HEX_WIDTH,
    height: HEX_HEIGHT + 4,
    paddingBottom: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  hexLabelWrap: {
    position: 'absolute',
    left: HEX_WIDTH * 0.1,
    right: HEX_WIDTH * 0.1,
    top: HEX_HEIGHT * 0.26,
    bottom: HEX_HEIGHT * 0.12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  hexInput: {
    width: '100%',
    backgroundColor: t.navyRaised,
    borderWidth: 1,
    borderColor: t.gold,
    borderRadius: 6,
    paddingHorizontal: 4,
    paddingVertical: 2,
    fontSize: 12,
    fontWeight: '700',
    textAlign: 'center',
    color: t.ivory,
  },
  hexLabelText: {
    fontSize: 11,
    lineHeight: 13,
    fontWeight: '800',
    color: t.ivory,
    textAlign: 'center',
    letterSpacing: 0.4,
  },
  hexArrowConnector: {
    position: 'absolute',
    top: HEX_HEIGHT / 2 - 8,
    left: HEX_WIDTH - 4,
    width: ARROW_LEN + 14,
    height: 16,
    flexDirection: 'row',
    alignItems: 'center',
    zIndex: 1,
  },
  stageColumn: {
    width: COL_WIDTH,
    alignItems: 'center',
    position: 'relative',
  },
  // Arrow sits on the shared circle centerline, anchored to the right edge
  // of this column and extending into the gap before the next column, so
  // it visually connects THIS circle to the NEXT one regardless of how many
  // sub-boxes either stage has stacked below it.
  arrowConnector: {
    position: 'absolute',
    top: CIRCLE_DIAM / 2 - 8,
    left: CIRCLE_RIGHT_EDGE,
    width: ARROW_LEN + 14,
    height: 16,
    flexDirection: 'row',
    alignItems: 'center',
    zIndex: 1,
  },
  arrowLine: {
    width: ARROW_LEN,
    height: 3,
    backgroundColor: t.ivoryFaint,
  },
  arrowHead: {
    fontSize: 12,
    lineHeight: 12,
    color: t.ivoryFaint,
    marginLeft: -1,
  },
  stageOrb: {
    width: CIRCLE_DIAM,
    height: CIRCLE_DIAM,
    borderRadius: CIRCLE_DIAM / 2,
    borderWidth: 2,
    borderColor: t.navyLine,
    boxShadow: `0 0 14px ${t.goldGlow}`,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
  },
  onStatusFill: { color: '#ffffff' },
  stageOrbText: {
    fontSize: 14,
    fontWeight: '700',
    textAlign: 'center',
  },
  subBoxContainer: {
    alignItems: 'center',
    marginTop: 0,
  },
  subBoxConnector: {
    width: 2,
    height: 14,
    backgroundColor: t.ivoryFaint,
  },
  subBoxConnectorShort: {
    width: 2,
    height: 10,
    backgroundColor: t.ivoryFaint,
  },
  subBox: {
    width: 84,
    height: 30,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 5,
  },
  subBoxText: {
    fontSize: 13,
    fontWeight: '700',
    textAlign: 'center',
  },
}),
);
